/**
 * Payables hub — the "what we owe" command centre.
 *
 * Aggregates three distinct liability sources into a single page:
 *   - Supplier obligations (PO totals, until AP/payments lands)
 *   - Business loans (formal bank debt)
 *   - Personal / other loans (staff advances, owner loans, supplier advances)
 *
 * Plus a unified payment activity log so you can see at a glance what's
 * been paid and when, regardless of which underlying ledger the entry
 * came from.
 *
 * Supplier payments can be recorded directly from this page via the "Pay"
 * button on each supplier row. Clicking a supplier row opens a detail sheet
 * showing payment history and a "Record payment" action. Loan rows still
 * navigate to /finance/loans.
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Building2,
  CreditCard,
  Plus,
  Receipt,
  Users,
  Wallet,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate, formatRelative } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useHasPermission } from "../../../lib/permissions";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockLoans,
  mockLoanRepayments,
  type MockLoan,
  type MockLoanRepayment,
} from "../../../lib/financeMock";
import {
  buildActivityLog,
  buildPayables,
  mockSupplierInvoices,
  mockSupplierPayments,
  summarisePayables,
  type PayableActivityEntry,
  type PayableRow,
  type PayablesCategory,
  type MockSupplierPayment,
} from "../../../lib/payablesMock";
import { mockPurchaseOrders } from "../../../components/imports/importsMockData";
import {
  SupplierPaymentForm,
  type SupplierPaymentFormValues,
} from "../../../components/forms/SupplierPaymentForm";

export const Route = createFileRoute("/_app/finance/payables")({
  component: PayablesPage,
});

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;

/* ─────────────────────────────────────────────────────────────
 * Tab config — each tab filters down to one category, plus an All view.
 * ───────────────────────────────────────────────────────────── */
const TABS: ReadonlyArray<{
  value: "all" | PayablesCategory;
  label: string;
  Icon: typeof Receipt;
}> = [
  { value: "all", label: "All", Icon: Wallet },
  { value: "supplier", label: "Suppliers", Icon: Receipt },
  { value: "business_loan", label: "Business loans", Icon: Building2 },
  { value: "personal_loan", label: "Personal & other", Icon: Users },
];

const CATEGORY_LABEL: Record<PayablesCategory, string> = {
  supplier: "Supplier",
  business_loan: "Business loan",
  personal_loan: "Personal / other",
};

const CATEGORY_BADGE: Record<
  PayablesCategory,
  "neutral" | "info" | "warning"
> = {
  supplier: "info",
  business_loan: "warning",
  personal_loan: "neutral",
};

function PayablesPage() {
  const navigate = useNavigate();
  const canSee = useHasPermission("finance.read");
  const canRecord = useHasPermission("payments.record");

  /* ── Live data ───────────────────────────────────────────────────────
   * Loans and loan repayments are read-only here (write path is /finance/loans).
   * Supplier payments ARE writable from this page — that's the new flow. */
  const [loans] = usePersistentMockState<MockLoan[]>("loans", mockLoans);
  const [loanRepayments] = usePersistentMockState<MockLoanRepayment[]>(
    "loanRepayments",
    mockLoanRepayments,
  );
  const [supplierPayments, setSupplierPayments] = usePersistentMockState<MockSupplierPayment[]>(
    "supplierPayments",
    mockSupplierPayments,
  );
  const [purchaseOrders] = usePersistentMockState<typeof mockPurchaseOrders>(
    "purchaseOrders",
    mockPurchaseOrders,
  );
  const [supplierInvoices] = usePersistentMockState<typeof mockSupplierInvoices>(
    "supplierInvoices",
    mockSupplierInvoices,
  );

  const [tab, setTab] = useState<"all" | PayablesCategory>("all");

  /**
   * The row whose detail sheet is currently open (supplier rows only).
   * `null` means the sheet is closed.
   */
  const [activeRow, setActiveRow] = useState<PayableRow | null>(null);

  /**
   * Whether the sheet body shows the detail view or the payment-recording
   * form. Resets to "detail" whenever a new row is activated.
   */
  const [detailSheetMode, setDetailSheetMode] = useState<"detail" | "form">(
    "detail",
  );

  /* ── Derived: payables list, summary, activity log. ─────────────────
   * supplierPayments drives the PO outstanding computation inside buildPayables
   * so the table automatically reflects newly recorded payments. */
  const allPayables = useMemo(
    () =>
      buildPayables({
        loans,
        purchaseOrders,
        supplierPayments,
        supplierInvoices,
      }),
    [loans, purchaseOrders, supplierPayments, supplierInvoices],
  );

  const filtered = useMemo(() => {
    if (tab === "all") return allPayables;
    return allPayables.filter((p) => p.category === tab);
  }, [allPayables, tab]);

  const summary = useMemo(
    () => summarisePayables(allPayables),
    [allPayables],
  );

  const activity = useMemo(
    () =>
      buildActivityLog({
        loans,
        loanRepayments,
        supplierPayments,
      }),
    [loans, loanRepayments, supplierPayments],
  );

  /* ── Handlers ────────────────────────────────────────────────────── */

  function handleRecordSupplierPayment(values: SupplierPaymentFormValues) {
    if (!activeRow) return;

    const year = new Date().getFullYear();
    const nextNum = String(supplierPayments.length + 1).padStart(6, "0");

    const payment: MockSupplierPayment = {
      _id: `spp_${Date.now()}`,
      paymentNumber: `SPP-${year}-${nextNum}`,
      supplierId: activeRow.supplierId ?? activeRow.sourceId,
      purchaseOrderId:
        activeRow.sourceKind === "purchase_order" ? activeRow.sourceId : null,
      occurredAt: new Date(values.occurredAt).getTime(),
      amountCents: values.amountCents,
      amountInBaseCents: values.amountInBaseCents,
      currency: values.currency,
      paymentMethod: values.paymentMethod,
      reference: values.reference || null,
    };

    setSupplierPayments((prev) => [...prev, payment]);
    // Return to detail view after recording; keep sheet open so user can
    // see the updated payment list immediately.
    setActiveRow(null);
    setDetailSheetMode("detail");
    toast.success(
      `Payment of BDT ${(values.amountInBaseCents / 100n).toLocaleString()} recorded.`,
    );
  }

  function handleOpenDetail(row: PayableRow) {
    setActiveRow(row);
    setDetailSheetMode("detail");
  }

  function handleOpenPaymentForm(row: PayableRow) {
    setActiveRow(row);
    setDetailSheetMode("form");
  }

  function handleCloseSheet() {
    setActiveRow(null);
    setDetailSheetMode("detail");
  }

  if (!canSee) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Payables"
          description="Everything we owe — suppliers, banks, and personal/staff loans — in one place."
        />
        <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-6 py-12 text-center">
          <p className="text-[13px] text-(--text-secondary)">
            You don't have permission to view this page.
          </p>
          <p className="mt-1 text-[11px] text-(--text-tertiary)">
            Payables is finance-only data. Ask an admin to grant access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Payables"
        description="Everything we owe — suppliers, banks, and personal/staff loans — in one place."
      />

      {/* KPI strip — total + 3-way breakdown. */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Payables summary"
      >
        <KpiCard
          caption="Total payable"
          value={formatMoneySmart(summary.totalBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`Across ${summary.itemCount} item${summary.itemCount === 1 ? "" : "s"}`}
        />
        <KpiCard
          caption="Suppliers"
          value={formatMoneySmart(summary.supplierBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint="PO commitments + AP"
        />
        <KpiCard
          caption="Business loans"
          value={formatMoneySmart(summary.businessLoanBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint="Bank / formal debt"
        />
        <KpiCard
          caption="Personal & other"
          value={formatMoneySmart(summary.personalLoanBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={
            summary.overdueOrDueSoonCount > 0
              ? `${summary.overdueOrDueSoonCount} due in 7 days`
              : "Staff / owner / advances"
          }
        />
      </section>

      {/* Tabs — All vs. category-specific filtered tables. */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="flex items-center gap-1.5"
            >
              <t.Icon className="size-3.5" aria-hidden />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-4">
            <PayablesTable
              rows={filtered}
              showCategory={t.value === "all"}
              canRecord={canRecord}
              onOpenRow={(row) => {
                if (row.sourceKind === "loan") {
                  void navigate({ to: "/finance/loans" });
                } else if (row.sourceKind === "purchase_order") {
                  void navigate({ to: "/imports/purchase-orders" });
                }
              }}
              onOpenDetail={handleOpenDetail}
              onRecordPayment={canRecord ? handleOpenPaymentForm : undefined}
            />
          </TabsContent>
        ))}
      </Tabs>

      {/* Activity log — payment history across loan repayments + supplier
          payments, newest first. Capped at 20 here; "View all" link for
          a future drill-down page. */}
      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[14px] font-semibold text-(--text-primary)">
            Recent payment activity
          </h2>
          <span className="text-[11px] text-(--text-tertiary)">
            {activity.length} payment{activity.length === 1 ? "" : "s"} on file
          </span>
        </div>
        <ActivityLog entries={activity.slice(0, 20)} />
      </section>

      {/* Supplier detail / payment sheet — opens for supplier row clicks and
          "Pay" button presses. Mode toggles between detail and form views. */}
      <Sheet
        open={activeRow !== null}
        onOpenChange={(open) => {
          if (!open) handleCloseSheet();
        }}
      >
        <SheetContent side="right" size="md">
          {activeRow ? (
            detailSheetMode === "detail" ? (
              <SupplierDetailView
                row={activeRow}
                supplierPayments={supplierPayments}
                canRecord={canRecord}
                onRecordPayment={() => setDetailSheetMode("form")}
                onNavigateToPO={() => {
                  void navigate({ to: "/imports/purchase-orders" });
                  handleCloseSheet();
                }}
                onClose={handleCloseSheet}
              />
            ) : (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-md
                        bg-(--accent-soft) text-(--accent)"
                    >
                      <Receipt className="h-4 w-4" />
                    </span>
                    Record payment
                  </SheetTitle>
                  <SheetDescription>
                    {activeRow.counterparty} · {activeRow.reference}
                  </SheetDescription>
                </SheetHeader>
                <SheetBody>
                  <SupplierPaymentForm
                    counterparty={activeRow.counterparty}
                    poReference={activeRow.reference}
                    outstandingBaseCents={activeRow.outstandingBaseCents}
                    onSubmit={handleRecordSupplierPayment}
                    onCancel={() => setDetailSheetMode("detail")}
                  />
                </SheetBody>
              </>
            )
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Supplier detail view — payment history + actions.
 * ───────────────────────────────────────────────────────────── */

interface SupplierDetailViewProps {
  row: PayableRow;
  supplierPayments: ReadonlyArray<MockSupplierPayment>;
  canRecord: boolean;
  onRecordPayment: () => void;
  onNavigateToPO: () => void;
  onClose: () => void;
}

function SupplierDetailView({
  row,
  supplierPayments,
  canRecord,
  onRecordPayment,
  onNavigateToPO,
  onClose,
}: SupplierDetailViewProps) {
  // Filter payments linked to this specific PO.
  const linkedPayments = useMemo(
    () =>
      supplierPayments
        .filter((p) => p.purchaseOrderId === row.sourceId)
        .sort((a, b) => b.occurredAt - a.occurredAt),
    [supplierPayments, row.sourceId],
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-md
              bg-(--accent-soft) text-(--accent)"
          >
            <Receipt className="h-4 w-4" />
          </span>
          Purchase Order
        </SheetTitle>
        <SheetDescription>{row.reference}</SheetDescription>
      </SheetHeader>

      <SheetBody className="flex flex-col gap-5">
        {/* Original / Outstanding amounts */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-md bg-(--bg-canvas) border border-(--border-subtle) px-3 py-3">
            <div className="text-[11px] text-(--text-tertiary) uppercase tracking-wide mb-1">
              Original
            </div>
            <div className="font-mono tabular-nums text-[15px] font-semibold text-(--text-secondary)">
              {formatMoneySmart(row.originalBaseCents, "BDT", {
                locale: "en-BD",
              })}
            </div>
          </div>
          <div className="rounded-md bg-(--bg-canvas) border border-(--border-subtle) px-3 py-3">
            <div className="text-[11px] text-(--text-tertiary) uppercase tracking-wide mb-1">
              Outstanding
            </div>
            <div className="font-mono tabular-nums text-[15px] font-semibold text-(--text-primary)">
              {formatMoneySmart(row.outstandingBaseCents, "BDT", {
                locale: "en-BD",
              })}
            </div>
          </div>
        </div>

        {/* Status + due date */}
        <div className="flex items-center gap-3">
          <StatusPill variant={statusVariant(row.statusLabel)} size="sm">
            {row.statusLabel}
          </StatusPill>
          {row.dueDate !== null ? (
            <span className="text-[12px] text-(--text-tertiary)">
              Due {formatDate(row.dueDate, { style: "short" })}
            </span>
          ) : null}
        </div>

        {/* Payments made section */}
        <div className="flex flex-col gap-2">
          <h3 className="text-[13px] font-semibold text-(--text-primary)">
            Payments made
          </h3>
          {linkedPayments.length === 0 ? (
            <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-4 py-6 text-center text-[12px] text-(--text-tertiary)">
              No payments yet
            </div>
          ) : (
            <ul className="rounded-md border border-(--border-subtle) bg-(--bg-surface) overflow-hidden divide-y divide-[var(--border-subtle)]">
              {linkedPayments.map((p) => (
                <li
                  key={p._id}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 px-3 py-2"
                >
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[12px] text-(--text-primary) font-medium font-mono">
                        {p.paymentNumber}
                      </span>
                      <span className="text-[11px] text-(--text-tertiary)">
                        {formatRelative(p.occurredAt)}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 text-[11px] text-(--text-tertiary)">
                      <span className="capitalize">
                        {p.paymentMethod.replace(/_/g, " ")}
                      </span>
                      {p.reference ? (
                        <>
                          <span aria-hidden>·</span>
                          <span className="italic truncate">{p.reference}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono tabular-nums text-[12px] text-(--text-primary)">
                      {formatMoney(p.amountInBaseCents, "BDT", {
                        locale: "en-BD",
                      })}
                    </div>
                    {p.currency !== "BDT" ? (
                      <div className="text-[10px] text-(--text-tertiary) tabular-nums">
                        {formatMoney(p.amountCents, p.currency, {
                          display: "code",
                          locale: "en-BD",
                        })}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Link to full PO */}
        <button
          type="button"
          className="flex items-center gap-1 text-[12px] text-(--accent) hover:underline underline-offset-2 w-fit"
          onClick={onNavigateToPO}
        >
          View full PO
          <ArrowUpRight className="size-3" aria-hidden />
        </button>
      </SheetBody>

      <SheetFooter>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
        >
          Close
        </Button>
        {canRecord ? (
          <Button
            size="sm"
            disabled={row.outstandingBaseCents <= 0n}
            onClick={onRecordPayment}
          >
            Record payment
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Payables table — DataTable with a flexible column for category.
 * ───────────────────────────────────────────────────────────── */
function PayablesTable({
  rows,
  showCategory,
  canRecord,
  onOpenRow,
  onOpenDetail,
  onRecordPayment,
}: {
  rows: PayableRow[];
  showCategory: boolean;
  /** Whether the current user has payments.record permission. */
  canRecord: boolean;
  /** Navigate to the underlying record (loan page or PO page). */
  onOpenRow: (row: PayableRow) => void;
  /** Open the detail sheet for a supplier row. */
  onOpenDetail: (row: PayableRow) => void;
  onRecordPayment?: (row: PayableRow) => void;
}) {
  const columns = useMemo<ColumnDef<PayableRow>[]>(() => {
    const cols: ColumnDef<PayableRow>[] = [
      {
        id: "reference",
        header: "Reference",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-mono text-[12px] text-(--text-primary)">
              {row.original.reference}
            </span>
            <span className="text-[11px] text-(--text-tertiary) uppercase tracking-wide">
              {row.original.sourceKind === "loan" ? "Loan" : "Purchase order"}
            </span>
          </div>
        ),
        meta: { mono: true },
      },
      {
        id: "counterparty",
        header: "Counterparty",
        cell: ({ row }) => (
          <span className="text-(--text-primary)">
            {row.original.counterparty}
          </span>
        ),
      },
    ];

    if (showCategory) {
      cols.push({
        id: "category",
        header: "Type",
        cell: ({ row }) => (
          <Badge
            variant={CATEGORY_BADGE[row.original.category]}
            size="sm"
          >
            {CATEGORY_LABEL[row.original.category]}
          </Badge>
        ),
      });
    }

    cols.push(
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill
            variant={statusVariant(row.original.statusLabel)}
            size="sm"
          >
            {row.original.statusLabel}
          </StatusPill>
        ),
      },
      {
        id: "dueDate",
        header: "Due",
        cell: ({ row }) => {
          const due = row.original.dueDate;
          if (due === null) {
            return <span className="text-(--text-tertiary)">—</span>;
          }
          const overdue = due < NOW;
          const dueSoon = !overdue && due - NOW <= 7 * DAY;
          return (
            <span
              className={cn(
                "tabular-nums text-[12px]",
                overdue
                  ? "text-[var(--danger)] font-semibold"
                  : dueSoon
                    ? "text-[var(--warning)]"
                    : "text-(--text-secondary)",
              )}
              title={
                overdue
                  ? "Past due"
                  : dueSoon
                    ? "Due within 7 days"
                    : undefined
              }
            >
              {formatDate(due, { style: "short" })}
            </span>
          );
        },
        meta: { numeric: false },
      },
      {
        id: "original",
        header: "Original",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoneySmart(row.original.originalBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "paid",
        header: "Paid",
        meta: { className: "w-28" },
        cell: ({ row }) => {
          const isSupplier = row.original.sourceKind === "purchase_order";
          if (!isSupplier) {
            return <span className="text-(--text-tertiary)">—</span>;
          }
          const original = row.original.originalBaseCents;
          const outstanding = row.original.outstandingBaseCents;
          const paidFraction =
            original > 0n
              ? Number(original - outstanding) / Number(original)
              : 0;
          const pctLabel = `${Math.round(paidFraction * 100)}% paid`;
          const barColor =
            paidFraction >= 0.8
              ? "bg-(--success)"
              : paidFraction >= 0.4
                ? "bg-[var(--warning)]"
                : "bg-[var(--danger)]";
          return (
            <div className="flex flex-col gap-0.5 w-full">
              <div className="relative h-[4px] w-full rounded-full bg-(--bg-canvas) overflow-hidden">
                <div
                  className={cn("h-full rounded-full", barColor)}
                  style={{ width: `${paidFraction * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-(--text-tertiary)">
                {pctLabel}
              </span>
            </div>
          );
        },
      },
      {
        id: "outstanding",
        header: "Outstanding",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-primary) font-semibold">
            {formatMoneySmart(row.original.outstandingBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const isSupplier = row.original.sourceKind === "purchase_order";
          const canPay =
            canRecord &&
            isSupplier &&
            row.original.outstandingBaseCents > 0n;
          return (
            <div className="flex items-center justify-end gap-1">
              {canPay ? (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRecordPayment?.(row.original);
                  }}
                  aria-label="Record payment"
                  title="Record payment"
                >
                  <Plus className="size-3" aria-hidden />
                  Pay
                </Button>
              ) : null}
              <button
                type="button"
                className="text-(--text-tertiary) hover:text-(--text-primary) p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenRow(row.original);
                }}
                aria-label="Open underlying record"
              >
                <ArrowUpRight className="size-3.5" />
              </button>
            </div>
          );
        },
        meta: { className: "w-24" },
      },
    );

    return cols;
  }, [showCategory, canRecord, onOpenRow, onRecordPayment]);

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(r) => r._id}
      onRowClick={(r) => {
        if (r.original.sourceKind === "purchase_order") {
          onOpenDetail(r.original);
        } else {
          onOpenRow(r.original);
        }
      }}
      emptyTitle="Nothing on the books"
      emptyDescription="No outstanding payables in this category."
      emptyIcon={<Wallet className="h-5 w-5" aria-hidden />}
    />
  );
}

/**
 * Map a free-form status label to a StatusPill variant. Keeps the colour
 * vocabulary consistent across PO and loan entries even though their
 * underlying state machines differ.
 */
function statusVariant(
  label: string,
): "neutral" | "info" | "success" | "warning" | "danger" {
  const lower = label.toLowerCase();
  if (lower === "active" || lower === "in transit") return "info";
  if (lower === "paid off" || lower === "received") return "success";
  if (lower === "draft" || lower === "submitted" || lower === "acknowledged")
    return "neutral";
  if (lower === "defaulted") return "danger";
  return "neutral";
}

/* ─────────────────────────────────────────────────────────────
 * Activity log — a chronological list of payments out.
 * ───────────────────────────────────────────────────────────── */
function ActivityLog({ entries }: { entries: PayableActivityEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-4 py-8 text-center text-[13px] text-(--text-tertiary)">
        No payments recorded yet.
      </div>
    );
  }

  return (
    <ul className="rounded-md border border-(--border-subtle) bg-(--bg-surface) overflow-hidden divide-y divide-[var(--border-subtle)]">
      {entries.map((e) => (
        <ActivityRow key={e._id} entry={e} />
      ))}
    </ul>
  );
}

function ActivityRow({ entry }: { entry: PayableActivityEntry }) {
  const Icon = entry.kind === "loan_repayment" ? CreditCard : Receipt;
  const tint =
    entry.category === "supplier"
      ? "text-[var(--info)] bg-[var(--info-soft)]"
      : entry.category === "business_loan"
        ? "text-[var(--warning)] bg-[var(--warning-soft)]"
        : "text-(--text-secondary) bg-(--bg-canvas)";

  return (
    <li className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 hover:bg-(--bg-hover) transition-colors duration-[var(--duration-instant)]">
      {/* Category icon chip */}
      <span
        className={cn(
          "inline-flex size-7 items-center justify-center rounded-full",
          tint,
        )}
        aria-hidden
      >
        <Icon className="size-3.5" />
      </span>

      {/* Counterparty + reference + method */}
      <div className="flex flex-col min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[13px] text-(--text-primary) font-medium truncate">
            {entry.counterparty}
          </span>
          <span className="text-[11px] text-(--text-tertiary) tabular-nums">
            {formatRelative(entry.occurredAt)}
          </span>
        </div>
        <div className="flex items-baseline gap-2 text-[11px] text-(--text-tertiary)">
          <span className="font-mono">{entry.reference}</span>
          <span aria-hidden>·</span>
          <span className="capitalize">
            {entry.paymentMethod.replace(/_/g, " ")}
          </span>
          {entry.note ? (
            <>
              <span aria-hidden>·</span>
              <span className="italic truncate">{entry.note}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Amount */}
      <div className="text-right">
        <div className="font-mono tabular-nums text-[13px] text-(--text-primary)">
          {formatMoney(entry.amountInBaseCents, "BDT", { locale: "en-BD" })}
        </div>
        {entry.currency !== "BDT" ? (
          <div className="text-[10px] text-(--text-tertiary) tabular-nums">
            {formatMoney(entry.amountCents, entry.currency, {
              display: "code",
              locale: "en-BD",
            })}
          </div>
        ) : null}
      </div>
    </li>
  );
}
